import { ChangeDetectorRef } from '@angular/core';
import { AbstractControl } from '@angular/forms';

import EXIF from 'exif-js';
import { merge, Observable, pluck } from 'rxjs';
import { filter, first, share, takeUntil } from 'rxjs/operators';

import { ExifStyles, IExif } from './exif.interfaces';
import { exif2css } from './exif2css';

type ExcludeFalsy<T> = T extends null | undefined | false | 0 | '' | void ? never : T;
export const filterBoolean = <T>(source: Observable<T>): Observable<ExcludeFalsy<T>> =>
  <Observable<ExcludeFalsy<T>>>source.pipe(filter(value => !!value));

export const formDetectChanges = (
  form: AbstractControl,
  cdRef: ChangeDetectorRef,
  onDestroy$: Observable<any>,
  { valueChanges, statusChanges } = { valueChanges: true, statusChanges: true },
) => {
  const observables = [];
  if (valueChanges) {
    observables.push(form.valueChanges);
  }
  if (statusChanges) {
    observables.push(form.statusChanges);
  }

  merge(...observables)
    .pipe(takeUntil(onDestroy$))
    .subscribe(() => cdRef.detectChanges());
};

export const fileToBase64 = (file: File): Observable<string> =>
  new Observable<string>(observer => {
    const fileReader = new FileReader();
    fileReader.onload = () => {
      observer.next(<string>fileReader.result);
      observer.complete();
    };
    fileReader.onerror = () => observer.error(fileReader.error);
    fileReader.onabort = () => observer.error(new Error('File reading aborted (base64)'));
    fileReader.readAsDataURL(file);

    return () => (fileReader.readyState !== 2 ? fileReader.abort() : null);
  }).pipe(share());

export const getImageExifData = (file: File): Observable<{ exif: IExif; styles: ExifStyles }> =>
  new Observable<{ exif: IExif; styles: ExifStyles }>(observer => {
    const fileReader = new FileReader();
    fileReader.onload = () => {
      const exif = EXIF.readFromBinaryFile(fileReader.result);
      observer.next({
        exif,
        styles: exif2css(exif.Orientation),
      });
      observer.complete();
    };
    fileReader.onerror = () => observer.error(fileReader.error);
    fileReader.onabort = () => observer.error(new Error('File reading aborted (array buffer)'));
    fileReader.readAsArrayBuffer(file);

    return () => (fileReader.readyState !== 2 ? fileReader.abort() : null);
  }).pipe(share());

export const firstPayload = <T>(source: Observable<{ payload: T }>): Observable<T> =>
  source.pipe(first(), pluck('payload'));
